import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr/immutable';
import { useCookies } from 'react-cookie';
import { useFormik } from 'formik';
import {
  createLicenseKey,
  editLicenseKey,
  getFullLicense,
} from '../../../api/bots';
import { COOKIE } from '../../../constants';
import OperatingSystemTag from '../../bots/common/OperatingSystemTag';
import { TidalButton } from '../../common/button';
import TidalDropdown from '../../common/dropdown/TidalDropdown';
import TidalDateField from '../../common/input/TidalDateInput';
import TidalInputField from '../../common/input/TidalInputField';
import TidalSuffixInputField from '../../common/input/TidalSuffixInputField';
import TidalTextBox from '../../common/input/TidalTextBox';
import { TidalTag } from '../../common/tag';
import { Box } from '../../Flex';
import TidalDashboardAddBotKeySearchInput from './TidalDashboardAddBotKeySearchInput';
import { toast } from 'react-toastify';
import Loading from '../../Loading';

const TidalDashboardEditBotKeyModalWrapper = styled.div`
  width: 833px;
  max-width: 100%;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 14px;
  padding: 23px 27px;
  max-height: 827px;
  overflow: hidden;
  @media only screen and (max-width: 676px) {
    padding: 16px;
  }
`;

const TidalDashboardEditBotKeyModalTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
`;

const TidalDashboardEditBotKeyModalTitle = styled.p`
  font-weight: 700;
  font-size: 24px;
  line-height: 29px;
  color: ${(props) => props.theme.colors.text[1]};
`;

const TidalDashboardAddBotKeyRequired = styled.div`
  position: relative;

  & > p {
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: ${(props) => props.theme.colors.text[0]};
  }
  & > span {
    position: absolute;
    left: -10px;
    bottom: -4px;
    color: ${(props) => props.theme.colors.text[3]};
  }
`;

const TidalDashboardAddBotKeyBodyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0 28px;
  margin-top: 16px;
  @media only screen and (max-width: 676px) {
    grid-template-columns: 1fr;

    & > div {
      overflow-y: auto;
      overflow-x: hidden;
      max-height: 700px;
      padding-right: 8px;
    }
  }
`;

const TidalDashboardEditBotKeyModalSubTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${(props) => props.theme.colors.text[1]};
  margin-bottom: 2px;
  position: relative;
  & > span {
    font-size: 14px;
    line-height: 17px;
    position: absolute;
    right: 0px;
    bottom: 0px;
    color: ${(props) => props.theme.colors.accent};
  }
`;

const TidalDashboardAddBoyKeyForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 0 16px;
  padding-top: 19px;
  margin-top: 12px;
  border-top: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  @media only screen and (max-width: 676px) {
    display: block;
    & > div:nth-child(2) {
      margin-top: 16px;
    }
  }
`;

const TidalDashboardAddBotKeyTagsRow = styled.div`
  display: flex;
  grid-gap: 10px;
  flex-wrap: wrap;
`;

const TidalDashboardAddNewBotKeyModalFooter = styled.div`
  margin-top: 24px;
  padding-top: 26px;
  border-top: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  display: grid;
  grid-template-columns: 125px 214px;
  justify-content: flex-end;
  grid-gap: 16px;
  @media only screen and (max-width: 676px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
  }
`;

const TidalDashboardAddNewBotKeySelectedBot = styled.div`
  display: grid;
  grid-template-columns: 114px 1fr;
  grid-gap: 19px;
  margin-top: 28px;
  @media only screen and (max-width: 676px) {
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
  }
  & > div > .bot-name {
    display: flex;
    margin-bottom: 17px;
    grid-gap: 7px;
    flex-wrap: wrap;
    & > p {
      font-weight: 700;
      font-size: 18px;
      line-height: 22px;
      color: ${(props) => props.theme.colors.text[2]};
    }
  }
  & > img {
    height: 114px;
    width: 114px;
    border-radius: 10px;
    background-color: black;
  }
`;

interface ITidalDashboardEditBotKeyModal {
  onClose: () => void;
  onAdded: () => void;
  botName: string;
  botImage: string;
  licenseKey: string;
  id: string;
  botId: string;
  purchasePrice: string;
  renewalPrice: string;
  renewalDate: string;
  notes: string[];
  renewalInterval: string;
  licenseType: string;
  licenseTypes: string[];
  renewalIntervalTypes: string[];
  tags: number[];
}

interface MyFormValues {
  licenseKey: string;
  purchasePrice: string;
  renewalDate: string;
}

const TidalDashboardEditBotKeyModal = ({
  onClose,
  onAdded,
  botImage,
  botName,
  licenseKey,
  id,
  botId,
  purchasePrice,
  renewalPrice,
  notes,
  licenseType,
  renewalDate,
  renewalInterval,
  licenseTypes,
  renewalIntervalTypes,
  tags,
}: ITidalDashboardEditBotKeyModal): JSX.Element => {
  const {
    data,
    error: err,
    isValidating,
    mutate,
  } = useSWR('/dashboard/licenses/software');
  const [fullLicenseKey, setFullLicenseKey] = useState('');
  const [isFullKeyLoading, setIsFullKeyLoading] = useState(false);

  const [licenseKeyState, setLicenseKey] = useState(licenseKey);
  const [isEditing, setisEditing] = useState(false);
  const [purchasePriceState, setPurchasePrice] = useState(purchasePrice);
  const [renewalDateState, setRenewalDate] = useState(
    renewalDate ? dayjs(new Date(renewalDate)) : dayjs(),
  );
  const [renewalPriceState, setRenewalPrice] = useState(renewalPrice);
  const [renewalIntervalState, setRenewalInterval] = useState(renewalInterval);
  const [notesState, setNotes] = useState(notes.join('\n'));
  const [notesArray, setNotesArray] = useState('');
  const [licenseTypeState, setLicenseType] = useState(licenseType);
  const [licenseTypesState, setLicenseTypes] = useState(licenseTypes);
  const [renewalIntervalsState, setRenewalIntervals] =
    useState(renewalIntervalTypes);
  const [selectedTag, setSelectedTag] = useState(-1);
  const [tagsState, setTags] = useState([
    {
      title: 'Holding',
      type: 'success',
      isSelected: false,
      value: 1,
    },
    {
      title: 'Using',
      type: 'primary',
      isSelected: false,
      value: 2,
    },
    {
      title: 'Renting',
      type: 'warning',
      isSelected: false,
      value: 3,
    },
    {
      title: 'Selling',
      type: 'info',
      isSelected: false,
      value: 4,
    },
    {
      title: 'Sold',
      type: 'error',
      isSelected: false,
      value: 5,
    },
  ]);

  const [bots, setBots] = useState(data);
  const [selectedBot, setSelectedBot] = useState({
    id: '',
    image_url: '',
    name: '',
    license_types: [],
  });

  useEffect(() => {
    if (data) {
      setBots(data);
      setSelectedBot(data[0]);
    }
  }, [data]);

  useEffect(() => {
    // initalizing values
    handleTagSelection({ value: tags[0] });

    setLicenseType(licenseType);
    setLicenseTypes(
      Array.from(
        new Set(
          licenseTypes.map((type) =>
            type.toLowerCase().includes('lifetime') ? 'lifetime' : 'renewal',
          ),
        ).values(),
      ),
    );
    handleGetFullLicenseKey();
  }, []);

  const handleTagSelection = (tag) => {
    const tempTags = [...tagsState];
    for (let i = 0; i < tempTags.length; i++) {
      if (tempTags[i].value == tag.value) {
        tempTags[i].isSelected = true;
        setSelectedTag(tempTags[i].value);
      } else {
        tempTags[i].isSelected = false;
      }
    }

    setTags(tempTags);
  };

  const handleGetFullLicenseKey = async () => {
    setIsFullKeyLoading(true);
    try {
      const res = await getFullLicense({
        licenseId: id,
      });
      setFullLicenseKey(res.licenseKey);
      setLicenseKey(res.licenseKey);
    } catch (e) {}
    setIsFullKeyLoading(false);
  };

  const handleEditBotLicenseKey = async () => {
    setisEditing(true);
    try {
      const res = await editLicenseKey({
        id,
        licenseKey: licenseKeyState,
        licenseType: licenseTypeState.toLowerCase().includes('lifetime')
          ? 'lifetime'
          : 'renewal',
        softwareId: botId,
        name: '',
        renewalDate: renewalDateState,
        renewalInterval: parseInt(renewalIntervalState) || 0,
        renewalPrice: parseInt(renewalPriceState) || 0,
        purchasePrice: parseInt(purchasePriceState),
        notes: [...notesState.split('\n')],
        tags: [selectedTag],
      });
      onAdded();
    } catch (e) {
      console.log(e);
    }
    setisEditing(false);
  };

  const validate = (values: MyFormValues) => {
    console.log(values);
    const errors: MyFormValues = {
      licenseKey: '',
      purchasePrice: '',
      renewalDate: '',
    };
    if (!values.licenseKey) {
      errors.licenseKey = 'Required';
    }
    console.log(errors);
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      licenseKey,
      purchasePrice,
      renewalDate,
    },
    isInitialValid: true,
    validate,
    onSubmit: (values) => {},
  });

  if (isValidating) return <Loading />;

  return (
    <TidalDashboardEditBotKeyModalWrapper>
      <TidalDashboardEditBotKeyModalTitleRow>
        <TidalDashboardEditBotKeyModalTitle>
          Edit Bot Key
        </TidalDashboardEditBotKeyModalTitle>
        <TidalDashboardAddBotKeyRequired>
          <p>Required Field</p>
          <span>*</span>
        </TidalDashboardAddBotKeyRequired>
      </TidalDashboardEditBotKeyModalTitleRow>
      <TidalDashboardAddBotKeyBodyRow>
        {bots && (
          <div>
            <TidalDashboardAddNewBotKeySelectedBot>
              <img alt="" src={`/assets/dashboard/bots/${botImage}`} />
              <div>
                <div className="bot-name">
                  <p>{botName}</p>
                  {/* <OperatingSystemTag text="windows" />
                  <OperatingSystemTag text="macos" /> */}
                </div>
                <TidalInputField
                  label="License Key"
                  isRequired={true}
                  name="licenseKey"
                  value={licenseKeyState}
                  onChange={(e: any) => {
                    formik.handleChange(e);
                    setLicenseKey(e.target.value);
                  }}
                  placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                />
                <p className="tidal-error">{formik.errors.licenseKey}</p>
              </div>
            </TidalDashboardAddNewBotKeySelectedBot>
            <TidalDashboardAddBoyKeyForm>
              <div>
                <TidalDropdown
                  label="License Type"
                  isRequired={true}
                  items={licenseTypesState}
                  defaultValue={licenseTypeState}
                  onSelect={(e) => {
                    setLicenseType(e);
                  }}
                />
                {licenseTypeState.toLowerCase() != 'lifetime' && (
                  <Box height={16} />
                )}
                {licenseTypeState.toLowerCase() != 'lifetime' && (
                  <TidalDateField
                    sel={renewalDateState}
                    setSel={(e) => {
                      setRenewalDate(e);
                    }}
                    label="Renewal Date"
                    isRequired={true}
                  />
                )}

                {licenseTypeState.toLowerCase() != 'lifetime' && (
                  <Box height={16} />
                )}
                {licenseTypeState.toLowerCase() != 'lifetime' && (
                  <TidalInputField
                    label="Renewal Days"
                    isRequired={false}
                    type="number"
                    defaultValue={renewalInterval}
                    onChange={(e: any) => {
                      formik.handleChange(e);
                      setRenewalInterval(e.target.value);
                    }}
                    placeholder="Enter renewal Days"
                  />
                  // <TidalDropdown
                  //   label="Renewal Interval"
                  //   isRequired={true}
                  //   defaultValue={renewalIntervalState}
                  //   items={renewalIntervalsState.map((_, i) => i.toString())}
                  //   onSelect={(e) => {
                  //     setRenewalInterval(e);
                  //   }}
                  // />
                )}
                <Box height={16} />
                <TidalDashboardEditBotKeyModalSubTitle>
                  Tags
                </TidalDashboardEditBotKeyModalSubTitle>
                <Box height={12} />
                <TidalDashboardAddBotKeyTagsRow>
                  {tagsState.map((tag, index) => (
                    <div
                      onClick={() => {
                        handleTagSelection(tag);
                      }}
                    >
                      <TidalTag
                        key={index}
                        title={tag.title}
                        type={tag.type}
                        enabled={tag.isSelected}
                      />
                    </div>
                  ))}
                </TidalDashboardAddBotKeyTagsRow>
              </div>
              <div>
                <TidalSuffixInputField
                  label="Purchase Price"
                  value={purchasePriceState}
                  name="purchasePrice"
                  onChange={(e) => {
                    formik.handleChange(e);
                    setPurchasePrice(e.target.value);
                  }}
                  type="number"
                  suffix="$"
                  placeholder="0"
                />
                {licenseTypeState.toLowerCase() != 'lifetime' && (
                  <Box height={16} />
                )}
                {licenseTypeState.toLowerCase() != 'lifetime' && (
                  <TidalSuffixInputField
                    label="Renewal Price"
                    suffix="$"
                    value={renewalPriceState}
                    type="number"
                    name="renewalPrice"
                    onChange={(e) => setRenewalPrice(e.target.value)}
                    placeholder="0"
                  />
                )}
                <Box height={16} />
                <TidalTextBox
                  label="Notes"
                  placeholder="Enter notes"
                  value={notesState}
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  height={156}
                />
              </div>
            </TidalDashboardAddBoyKeyForm>
            <TidalDashboardAddNewBotKeyModalFooter>
              <TidalButton
                text="Cancel"
                type="default"
                tint={true}
                onClick={onClose}
              />
              <TidalButton
                text="Edit Bot Key"
                type="info"
                tint={false}
                disabled={!formik.isValid && isEditing}
                isLoading={isEditing}
                onClick={handleEditBotLicenseKey}
              />
            </TidalDashboardAddNewBotKeyModalFooter>
          </div>
        )}
      </TidalDashboardAddBotKeyBodyRow>
    </TidalDashboardEditBotKeyModalWrapper>
  );
};

export default TidalDashboardEditBotKeyModal;
